/**
 * World: the ECS container + HEX tile grid + player economy. Holds all
 * component stores as typed Maps. Systems read/write these directly.
 *
 * The grid is flat-top axial hexes. Entities live in continuous world-space
 * (Transform.pos is world units, NOT a hex index); the hex grid is used only
 * for terrain, pathing, building/resource occupancy, and picking.
 *
 * Pure data + deterministic logic. No DOM, no rendering. The renderer reads
 * world state each frame but never mutates it.
 */
import type { Eid, PlayerState, SimEvent, Vec2 } from "./types";
import { RNG } from "./rng";
import { SpatialIndex } from "./spatial";
import { HEX_DIRECTIONS, MAX_CLIMB, type Hex } from "./hex";
import type {
  Building,
  Builder,
  Combat,
  Fauna,
  Garrison,
  Gatherer,
  Healer,
  Health,
  Ident,
  LineOfSight,
  Move,
  Owner,
  Producer,
  Rally,
  Resource,
  Transform,
} from "./components";
import type { MoveDomain } from "./naval";

export class World {
  rng: RNG;
  readonly cols: number;
  readonly rows: number;
  readonly size: number; // hex outer radius (world units)
  readonly seed: number; // map-gen seed — kept so a snapshot can regenerate terrain
  tick = 0;

  /** Generated hex grid, indexed col*rows + row (matches generateHexGrid's col-major layout). */
  hexes: Hex[];
  /** 1 = occupied: cannot BUILD here (water, mountain, building, resource node). */
  blocked: Uint8Array;
  /** Hex keys occupied by a resource node. These block building but NOT
   *  movement — units weave through forests instead of routing around them. */
  resourceHexKeys = new Set<number>();
  /** Hex keys occupied by a LAND building. Like resource hexes, these block
   *  BUILD placement but stay PASSABLE for movement: a building only fills a
   *  sub-tile circle (its model footprint in propPos), so units squeeze around
   *  small houses and route around big ones — sub-tile collision, not a tile wall. */
  buildingHexKeys = new Set<number>();
  /** Hex keys occupied by a completed SOLID structure. A dock key blocks its
   * water tile for ships; land-wall keys feed renderer connectivity and shared
   * continuous rampart geometry, not whole-tile movement blocking. */
  solidHexKeys = new Set<number>();
  /** Hex keys occupied by a GATE → its owner player. The tile itself remains
   * passable; shared rampart geometry uses this ownership to open/close only the
   * physical doorway on the continuous navigation submesh. */
  gateHexKeys = new Map<number, number>();
  /** Per-hex integer elevation tier (hex.level), flattened for fast lookup. */
  elev: Int16Array;
  /** hex key -> A* routing penalty for a resource-node footprint on that hex
   *  (rocks cost more than trunks). Fields/walkable props are absent (cost 0). */
  propCost = new Map<number, number>();
  /** hex key -> a prop's ACTUAL position + footprint radius, so path-smoothing
   *  can line-of-sight test against the real circle (props are scattered off the
   *  tile centre), not just the whole tile. */
  propPos = new Map<number, { x: number; z: number; r: number }>();

  players = new Map<number, PlayerState>();

  // ---- component stores (typed Maps; iterate + .has() for queries) ----
  transform = new Map<Eid, Transform>();
  owner = new Map<Eid, Owner>();
  ident = new Map<Eid, Ident>();
  health = new Map<Eid, Health>();
  move = new Map<Eid, Move>();
  combat = new Map<Eid, Combat>();
  gatherer = new Map<Eid, Gatherer>();
  builder = new Map<Eid, Builder>();
  producer = new Map<Eid, Producer>();
  resource = new Map<Eid, Resource>();
  building = new Map<Eid, Building>();
  rally = new Map<Eid, Rally>();
  los = new Map<Eid, LineOfSight>();
  healer = new Map<Eid, Healer>();
  fauna = new Map<Eid, Fauna>();
  /** Transport/garrison carriers → their cargo (naval transports). */
  garrison = new Map<Eid, Garrison>();
  /** Eids of dead animals turned into gatherable meat (rendered lying down). */
  carcass = new Set<Eid>();

  /** eids scheduled for removal at end of step. */
  private dead = new Set<Eid>();
  /** events emitted this step (consumed by renderer). */
  events: SimEvent[] = [];

  /** lookups derived during map setup */
  dropsitesByPlayer = new Map<number, Set<Eid>>();

  /** Transient per-tick spatial index over all transforms (NOT serialized —
   *  rebuilt on demand). Powers O(n) proximity queries (combat/healing/auras/bot). */
  readonly spatial = new SpatialIndex();

  private nextEid = 1;

  constructor(cols: number, rows: number, size: number, hexes: Hex[], seed: number) {
    this.cols = cols;
    this.rows = rows;
    this.size = size;
    this.seed = seed;
    this.hexes = hexes;
    this.blocked = new Uint8Array(cols * rows);
    this.elev = new Int16Array(cols * rows);
    for (const h of hexes) this.elev[this.hkey(h.col, h.row)] = h.level;
    this.rng = new RNG(seed);
  }

  /** Elevation tier of a hex (hex.level); -999 out of bounds. */
  levelAt(col: number, row: number): number {
    return this.inBounds(col, row) ? this.elev[this.hkey(col, row)] : -999;
  }

  /** Can a unit step DIRECTLY between two adjacent hexes? Only when the
   *  elevation difference is within MAX_CLIMB tiers — a steeper face is a cliff
   *  and must be walked around. Occupancy (water/buildings) is checked
   *  separately by isPassable. */
  canClimb(c1: number, r1: number, c2: number, r2: number): boolean {
    return Math.abs(this.levelAt(c1, r1) - this.levelAt(c2, r2)) <= MAX_CLIMB;
  }

  eid(): Eid {
    return this.nextEid++;
  }

  /** Rebuild the spatial index from current transforms. Systems that do proximity
   *  queries call this at their start, so the index reflects live positions + the
   *  current entity set. Cell ~a few tiles wide (most queries span a couple cells). */
  rebuildSpatial(): void {
    this.spatial.rebuild(Math.max(this.size * 4, 1), this.transform);
  }

  /** Entity-id counter — snapshot/restore it so resumed games keep allocating
   *  fresh ids (never colliding with restored entities). */
  getEidCounter(): number {
    return this.nextEid;
  }
  setEidCounter(n: number): void {
    this.nextEid = n;
  }

  /** Linear index for a hex (col,row). */
  hkey(col: number, row: number): number {
    return col * this.rows + row;
  }

  hexOf(col: number, row: number): Hex | undefined {
    if (!this.inBounds(col, row)) return undefined;
    return this.hexes[this.hkey(col, row)];
  }

  inBounds(col: number, row: number): boolean {
    return col >= 0 && row >= 0 && col < this.cols && row < this.rows;
  }

  /** Buildable/spawnable: nothing occupies the hex (incl. resource nodes). */
  isWalkable(col: number, row: number): boolean {
    if (!this.inBounds(col, row)) return false;
    return this.blocked[this.hkey(col, row)] === 0;
  }

  /** Passable for MOVEMENT/pathfinding: a tile is passable unless it's genuinely
   *  impassable TERRAIN (water is `blocked`). EVERY building — including a wall — is
   *  a sub-tile MODEL, not a whole-tile block: its tile reads as passable (you stand
   *  on the free space beside the model, like any house/tree) and the actual model
   *  collision (footprint circle for buildings, thin rampart BAR for walls — applied
   *  in movement AND pathfinding) is what stops you walking through it. A dock pier
   *  sits on water (already `blocked` here) and blocks SHIPS via the naval branch of
   *  passableFor (solidHexKeys) — so walls are no longer a special whole-tile case. */
  isPassable(col: number, row: number): boolean {
    if (!this.inBounds(col, row)) return false;
    const k = this.hkey(col, row);
    return this.blocked[k] === 0 || this.resourceHexKeys.has(k) || this.buildingHexKeys.has(k);
  }

  /** Water hex (sea or deep sea) — naturally impassable but valid for docks. */
  isWater(col: number, row: number): boolean {
    const h = this.hexOf(col, row);
    return !!h && (h.biome === "water" || h.biome === "deepWater");
  }

  /** A water hex that touches at least one non-water hex — where docks go. */
  isWaterEdge(col: number, row: number): boolean {
    if (!this.isWater(col, row)) return false;
    for (const [nc, nr] of this.neighbors(col, row))
      if (!this.isWater(nc, nr)) return true;
    return false;
  }

  /** Movement passability for a domain. LAND = the usual `isPassable` (building
   * and resource tiles remain usable around their sub-tile models). NAVAL = any water tile (ships sail the
   *  sea; land + land-buildings are impassable to them). The two domains are
   *  complementary, so a ship can never wander onto land and vice-versa. */
  passableFor(domain: MoveDomain, col: number, row: number, _forPlayer?: number): boolean {
    if (domain === "naval") {
      // ships sail open sea, but NOT into a solid pier (a dock on the water tile)
      return this.isWater(col, row) && !this.solidHexKeys.has(this.hkey(col, row));
    }
    return this.isPassable(col, row);
  }

  /** Terrain-only passability for fine MOVEMENT (NOT pathfinding). A wall is a
   *  sub-tile MODEL (a thin rampart bar), not a whole-tile block — so a unit may
   *  stand on the free part of a wall's tile; the bar collision (movementSystem)
   *  is what stops it crossing the rampart. Here we block only genuine terrain:
   *  water for land units (and a dock pier for ships). Gate access is resolved by
   *  its team-aware physical portal in rampart geometry, never by whole-tile state. */
  terrainPassable(domain: MoveDomain, col: number, row: number, _forPlayer?: number): boolean {
    if (domain === "naval") {
      return this.isWater(col, row) && !this.solidHexKeys.has(this.hkey(col, row));
    }
    if (this.isWater(col, row)) return false;
    return true;
  }

  /** The open WATER hex a dock's ships rally to: nearest water neighbour of the
   *  dock origin (deterministic). Mirror of dockShore, on the sea side, so a
   *  trained ship clears the pier instead of being told to move onto land. */
  dockWater(col: number, row: number): [number, number] | null {
    const c0 = this.hexCenter(col, row);
    let best: [number, number] | null = null, bestD = Infinity;
    for (const [nc, nr] of this.neighbors(col, row)) {
      if (!this.isWater(nc, nr)) continue;
      const c = this.hexCenter(nc, nr);
      const d = (c.x - c0.x) ** 2 + (c.y - c0.y) ** 2;
      // tie-break by lower (col,row) so the pick is iteration-order-independent
      if (d < bestD || (d === bestD && (nc < (best?.[0] ?? Infinity) || (nc === best?.[0] && nr < best[1])))) {
        bestD = d; best = [nc, nr];
      }
    }
    return best;
  }

  /** The land hex a dock on (col,row) attaches to: the nearest non-water
   *  neighbour to the dock centre (preferring a walkable one a builder can stand
   *  on). Deterministic, so the rendered gangway and the builders' stand-point
   *  agree — otherwise the pier points at one shore while workers build from
   *  another. */
  dockShore(col: number, row: number): [number, number] | null {
    const c0 = this.hexCenter(col, row);
    let best: [number, number] | null = null, bestD = Infinity;
    let fb: [number, number] | null = null, fbD = Infinity;
    for (const [nc, nr] of this.neighbors(col, row)) {
      if (this.isWater(nc, nr)) continue;
      const c = this.hexCenter(nc, nr);
      const d = (c.x - c0.x) ** 2 + (c.y - c0.y) ** 2;
      if (this.isWalkable(nc, nr)) { if (d < bestD) { bestD = d; best = [nc, nr]; } }
      else if (d < fbD) { fbD = d; fb = [nc, nr]; }
    }
    return best ?? fb;
  }

  setBlocked(col: number, row: number, v: boolean) {
    if (this.inBounds(col, row)) this.blocked[this.hkey(col, row)] = v ? 1 : 0;
  }

  /** 6 axial neighbor (col,row) pairs within bounds. */
  neighbors(col: number, row: number): [number, number][] {
    const [q, r] = this.axial(col, row);
    const out: [number, number][] = [];
    for (const [dq, dr] of HEX_DIRECTIONS) {
      const nq = q + dq;
      const nr = r + dr;
      const [nc, nrRow] = this.offset(nq, nr);
      if (this.inBounds(nc, nrRow)) out.push([nc, nrRow]);
    }
    return out;
  }

  /** (col,row) -> axial (q,r). */
  axial(col: number, row: number): [number, number] {
    const q = col;
    const r = row - ((col - (col & 1)) >> 1);
    return [q, r];
  }

  /** axial (q,r) -> (col,row). */
  offset(q: number, r: number): [number, number] {
    const col = q;
    const row = r + ((q - (q & 1)) >> 1);
    return [col, row];
  }

  markDead(eid: Eid) {
    this.dead.add(eid);
  }

  /** Turn a killed animal into a gatherable meat carcass IN PLACE: drop its
   *  living components (move/combat/health/fauna), lay it down, and attach a food
   *  Resource so workers can harvest it. Returns false if it wasn't an animal. */
  toCarcass(eid: Eid): boolean {
    const f = this.fauna.get(eid);
    if (!f) return false;
    this.fauna.delete(eid);
    this.move.delete(eid);
    this.combat.delete(eid);
    this.health.delete(eid);
    this.carcass.add(eid);
    this.resource.set(eid, { resource: "food", amount: f.food, perGather: 4, gatherTime: 1 });
    return true;
  }

  /** Remove a unit/building/resource: clear components + unblock its hex. */
  destroy(eid: Eid) {
    // A sunk transport takes its cargo with it: the occupants have no transform of
    // their own (they're inside the ship), so nothing else would ever remove them.
    // markDead each so flushDead's loop destroys them too — destroy() then
    // decrements their population the normal unit way (kind === "unit").
    const gar = this.garrison.get(eid);
    if (gar) for (const occ of gar.occupants) this.markDead(occ);

    const b = this.building.get(eid);
    if (b) {
      // buildings occupy their origin hex — but a dock sat on a water tile that
      // was (and must stay) impassable, so don't "free" water back to walkable.
      if (!this.isWater(b.origin.x, b.origin.y)) this.setBlocked(b.origin.x, b.origin.y, false);
      const bk = this.hkey(b.origin.x, b.origin.y);
      this.resourceHexKeys.delete(bk); // fields
      this.buildingHexKeys.delete(bk); // sub-tile footprint
      this.solidHexKeys.delete(bk); // dock pier / wall hard obstacle
      this.gateHexKeys.delete(bk); // gate opening
      this.propCost.delete(bk);
      this.propPos.delete(bk);
    } else if (this.ident.get(eid)?.kind === "resource") {
      // ONLY a resource node owns the hex under it (it registered blocked +
      // resourceHexKeys + propCost/propPos at spawn). Units and animals/carcasses
      // never block a hex — they only STAND on one (forests are passable), so
      // freeing "the hex under the entity" for them would wipe a still-standing
      // resource node that shares that tile, corrupting occupancy + pathing (and
      // it persists through save, since `blocked` is serialized verbatim).
      const t = this.transform.get(eid);
      if (t) {
        const hex = this.hexAtWorld(t.pos.x, t.pos.y);
        if (hex) {
          // a fish school sits on WATER, which must STAY impassable to land units
          // after the school is fished out — never free a water hex.
          if (!this.isWater(hex.col, hex.row)) this.setBlocked(hex.col, hex.row, false);
          this.resourceHexKeys.delete(this.hkey(hex.col, hex.row));
          this.propCost.delete(this.hkey(hex.col, hex.row));
          this.propPos.delete(this.hkey(hex.col, hex.row));
        }
      }
    }
    const owner = this.owner.get(eid)?.player;
    if (owner !== undefined) {
      this.dropsitesByPlayer.get(owner)?.delete(eid);
      const ps = this.players.get(owner);
      if (ps) {
        if (b?.populationCap) ps.populationCap = Math.max(0, ps.populationCap - b.populationCap);
        // a dead unit frees its population slot (was never decremented → pop count
        // crept up forever as units died).
        const idn = this.ident.get(eid);
        if (idn?.kind === "unit") ps.population = Math.max(0, ps.population - (idn.population ?? 1));
      }
    }
    this.transform.delete(eid);
    this.owner.delete(eid);
    this.ident.delete(eid);
    this.health.delete(eid);
    this.move.delete(eid);
    this.combat.delete(eid);
    this.gatherer.delete(eid);
    this.builder.delete(eid);
    this.producer.delete(eid);
    this.resource.delete(eid);
    this.building.delete(eid);
    this.rally.delete(eid);
    this.los.delete(eid);
    this.healer.delete(eid);
    this.fauna.delete(eid);
    this.garrison.delete(eid);
    this.carcass.delete(eid);
  }

  /** Flush all dead entities. Called at end of each step. */
  flushDead() {
    for (const eid of this.dead) this.destroy(eid);
    this.dead.clear();
  }

  emit(ev: SimEvent) {
    ev.tick = this.tick;
    this.events.push(ev);
  }

  /** World (x, y-as-z) -> nearest hex, or null if outside the grid. */
  hexAtWorld(x: number, z: number): Hex | null {
    const size = this.size;
    const q = (2 / 3) * (x / size);
    const r = (-1 / 3) * (x / size) + (Math.sqrt(3) / 3) * (z / size);
    // cube round
    const s = -q - r;
    let rq = Math.round(q);
    let rr = Math.round(r);
    const rs = Math.round(s);
    const qd = Math.abs(rq - q);
    const rd = Math.abs(rr - r);
    const sd = Math.abs(rs - s);
    if (qd > rd && qd > sd) rq = -rr - rs;
    else if (rd > sd) rr = -rq - rs;
    const [col, row] = this.offset(rq, rr);
    return this.hexOf(col, row) ?? null;
  }

  /** World-space center of a hex (col,row). */
  hexCenter(col: number, row: number): Vec2 {
    const h = this.hexes[this.hkey(col, row)];
    return { x: h.x, y: h.z };
  }
}
